import React, { useState } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  useTheme,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Container from "@mui/material/Container";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { logger } from "../../../utils/logger";
import {
  validarEmail,
  validarCampoRequerido,
} from "../../shared/utils/validaciones";
import { getSafeLastPath } from "../../shared/utils/navigation";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const navigate = useNavigate();
  const { login } = useAuth();
  const theme = useTheme();

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    // Validar email
    if (!validarCampoRequerido(email)) {
      errors.email = "El email es requerido";
    } else if (!validarEmail(email)) {
      errors.email = "El email no tiene un formato válido";
    }

    // Validar contraseña
    if (!validarCampoRequerido(password)) {
      errors.password = "La contraseña es requerida";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setValidationErrors({});

    // Validar formulario antes de enviar
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const usuario = await login(email, password);
      if (usuario) {
        navigate(getSafeLastPath(usuario.rol), { replace: true });
      } else {
        setError("Credenciales inválidas");
      }
    } catch (err) {
      logger.error("Error en login:", err);
      setError("Error al intentar iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "linear-gradient(-45deg, #2196f3, #21cbf3, #1de9b6, #a7ffeb)",
        backgroundSize: "400% 400%",
        animation: "gradientShift 15s ease infinite",
        "@keyframes gradientShift": {
          "0%": { backgroundPosition: "0% 50%" },
          "50%": { backgroundPosition: "100% 50%" },
          "100%": { backgroundPosition: "0% 50%" },
        },
        px: 2,
        transition: "background-color 0.3s ease, color 0.3s ease",
      }}
    >
      <Container maxWidth="xs">
        <Box
          sx={{
            width: "100%",
            transition: "transform 0.3s ease, opacity 0.3s ease",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              bgcolor: theme.palette.background.paper,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
              backdropFilter: "blur(10px)",
              transition:
                "background-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
            }}
          >
            <Box
              component="img"
              src="/brand/logo.png"
              alt="Logo"
              sx={{
                maxWidth: 260,
                mb: 3,
                filter: "drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1))",
                transition: "transform 0.3s ease, opacity 0.3s ease",
              }}
            />

            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                textAlign: "center",
                mb: 3,
                fontSize: { xs: "1.25rem", sm: "1.5rem" },
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              GESTOR DE RESERVAS
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: "100%", mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ width: "100%" }}
            >
              <TextField
                id="email"
                label="Email"
                type="email"
                fullWidth
                required
                margin="normal"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setEmail(e.target.value);
                  // Limpiar error de validación al escribir
                  if (validationErrors.email) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      email: undefined,
                    }));
                  }
                }}
                disabled={loading}
                placeholder="ejemplo@679inn.com.ar"
                error={!!validationErrors.email}
                helperText={validationErrors.email}
              />

              <TextField
                id="password"
                label="Contraseña"
                type={showPassword ? "text" : "password"}
                fullWidth
                required
                margin="normal"
                autoComplete="current-password"
                value={password}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setPassword(e.target.value);
                  // Limpiar error de validación al escribir
                  if (validationErrors.password) {
                    setValidationErrors((prev) => ({
                      ...prev,
                      password: undefined,
                    }));
                  }
                }}
                disabled={loading}
                error={!!validationErrors.password}
                helperText={validationErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  borderRadius: 2,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  textTransform: "none",
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    background: theme.palette.grey[300],
                    color: theme.palette.grey[500],
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Iniciar Sesión"
                )}
              </Button>
            </Box>

            <Typography
              variant="body2"
              sx={{
                mt: 3,
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontSize: "0.875rem",
              }}
            >
              Sistema de Gestión de Reservas 679inn
            </Typography>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Login;
